import { useCallback, useMemo } from 'react';

import { Stars01Icon } from '@/components/icons/Stars01Icon';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { MenuItem } from '@/components/poppers/MenuPopper/MenuItem';
import { useReportError } from '@/hooks/useReportError';
import { isAIDPWorkflowEnabled } from '@/modules/aiDP/aidp.utils';
import { useDispositiveProvisionSuggestionsContext } from '@/modules/aiDP/context/dispositiveProvisionSuggestions.context';
import { useDismissAIDPSuggestions } from '@/modules/aiDP/hooks/useDismissAIDPSuggestions';
import {
  TriggerDPAISuggestionsVariant,
  useStartAIDPWorkflow,
} from '@/modules/aiDP/hooks/useStartAIDPWorkflow';
import { useStartAIDPWorkflowForTE } from '@/modules/aiDP/hooks/useStartAIDPWorkflowForTE';
import { useDispositiveProvisionsContext } from '@/modules/dispositiveProvisions/contexts/dispositiveProvisions.context';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { COLORS } from '@/styles/tokens/colors';
import { getPulidKind, PulidKind } from '@/utils/pulid';

/**
 * A menu item component for regenerating AI suggestions for dispositive provisions
 */
export function RegenerateAIDPMenuItem() {
  const { reportError } = useReportError();
  const { showFeedback } = useFeedback();

  const { householdId } = useHouseholdDetailsContext();

  const {
    isTestamentaryEntity,
    isClientProfile,
    entityType,
    clientProfileOrEntityOrTestamentaryEntityId,
  } = useDispositiveProvisionsContext();

  const { dismissSuggestions } = useDismissAIDPSuggestions();

  const { startWorkflow, isStartingWorkflow } = useStartAIDPWorkflow({
    variant: TriggerDPAISuggestionsVariant.SINGLE_ENTITY,
  });

  const { startWorkflow: startTEWorkflow } = useStartAIDPWorkflowForTE();

  const { refetchSuggestions, suggestionDocumentIds } =
    useDispositiveProvisionSuggestionsContext();

  const showRegenAIDP = useMemo(() => {
    if (isClientProfile) return false;
    if (isTestamentaryEntity) return true;
    return isAIDPWorkflowEnabled(entityType);
  }, [entityType, isClientProfile, isTestamentaryEntity]);

  const handleClick = useCallback(async () => {
    const idKind = getPulidKind(clientProfileOrEntityOrTestamentaryEntityId);

    try {
      await dismissSuggestions();

      if (isTestamentaryEntity && idKind === PulidKind.TestamentaryEntity) {
        await startTEWorkflow(
          [clientProfileOrEntityOrTestamentaryEntityId],
          suggestionDocumentIds
        );
        void refetchSuggestions(clientProfileOrEntityOrTestamentaryEntityId);
        return;
      }

      if (idKind == PulidKind.Entity) {
        await startWorkflow({
          householdId,
          entityId: clientProfileOrEntityOrTestamentaryEntityId,
        });
        void refetchSuggestions(clientProfileOrEntityOrTestamentaryEntityId);
        return;
      }

      reportError(
        `clicked regen ai dp workflow for unsupported id kind. id=${clientProfileOrEntityOrTestamentaryEntityId}, kind=${idKind}`
      );
      return;
    } catch (error) {
      reportError(
        'Error regenerating AI dispositive provision suggestions',
        error as Error
      );
      showFeedback('Error regenerating AI dispositive provision suggestions');
    }
  }, [
    clientProfileOrEntityOrTestamentaryEntityId,
    dismissSuggestions,
    householdId,
    isTestamentaryEntity,
    refetchSuggestions,
    reportError,
    showFeedback,
    startTEWorkflow,
    startWorkflow,
    suggestionDocumentIds,
  ]);

  if (!showRegenAIDP) {
    return null;
  }

  return (
    <MenuItem
      icon={<Stars01Icon size={16} color={COLORS.TEAL[600]} />}
      label="Regenerate AI suggestions"
      onClick={handleClick}
      disabled={isStartingWorkflow}
      muiMenuItemProps={{
        divider: true,
      }}
    />
  );
}
